import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface RejectionDialogProps {
  open: boolean;
  ruleName?: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const RejectionDialog: React.FC<RejectionDialogProps> = ({
  open,
  ruleName,
  onConfirm,
  onCancel
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(false);

  const handleConfirm = () => {
    // Validate: reason must not be empty
    if (!reason.trim()) {
      setError(true);
      return;
    }
    
    // If valid, proceed
    onConfirm(reason.trim());
    setReason('');
    setError(false);
  };

  const handleClose = () => {
    setReason('');
    setError(false);
    onCancel();
  };

  const handleReasonChange = (value: string) => {
    setReason(value);
    if (error && value.trim()) {
      setError(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          backgroundColor: '#fff'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      {/* Header - NO BORDER */}
      <DialogTitle 
        sx={{ 
          pb: 2,
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#000',
            fontSize: '20px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          Reject Rule
        </Typography>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Message and input field with validation */}
      <DialogContent sx={{ pb: 2, px: 3 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#555',
            fontSize: 15,
            lineHeight: 1.6,
            mb: 2,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          Reason for rejecting rule <strong>{ruleName}</strong>
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Enter rejection reason..."
          value={reason}
          onChange={(e) => handleReasonChange(e.target.value)}
          autoFocus
          error={error}
          helperText={error ? 'Please enter a rejection reason' : ''}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: '#fff',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
            },
            '& .MuiFormHelperText-root': {
              color: '#d32f2f',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
              fontWeight: 500
            }
          }}
        />
      </DialogContent>

      {/* Confirm button only - NO BORDER */}
      <DialogActions 
        sx={{ 
          p: 2,
          pt: 3,
          gap: 1,
          backgroundColor: '#fff',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          onClick={handleConfirm}
          sx={{
            backgroundColor: '#f44336',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontSize: 16,
            borderRadius: '8px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            '&:hover': {
              backgroundColor: '#d32f2f'
            }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectionDialog;
